import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AdminCapabilitiesResult,
  resolveAdminCapabilities,
  resolveAdminCapabilitiesFromMetadata,
} from "@equipped/core/admin/adminCapabilities";
import {
  addAdminProvider,
  AdminProviderConnection,
  AdminProviderRegistry,
  normalizeAdminBaseUrl,
  resolveActiveAdminProvider,
  setActiveAdminProvider,
} from "@equipped/core/admin/adminProviderRegistry";
import {
  AdminProviderRef,
  ProviderDeployment,
  resolveProviderDeployment,
} from "@equipped/core/admin/providerDeployment";
import { BackendClient, BackendError } from "@equipped/core/backend/client";
import { ADMIN_PROVIDER_CONFIG } from "../config/adminDeployment.config";
import { LocalAdminProviderRegistryStore } from "../lib/providers/localAdminProviderRegistryStore";

interface AddProviderInput {
  label: string;
  baseUrl: string;
}

interface ProviderState {
  deployment: ProviderDeployment;
  registry: AdminProviderRegistry;
  provider: AdminProviderRef | null;
  activeConnection: AdminProviderConnection | null;
  capabilities: AdminCapabilitiesResult | null;
  capabilitiesLoading: boolean;
  capabilitiesWarning: string | null;
  setActiveProviderId: (providerId: string) => Promise<void>;
  addProvider: (input: AddProviderInput) => Promise<void>;
  setProviderAuth: (kind: "session" | "adminKey", hasCredential: boolean) => void;
}

const ProviderContext = createContext<ProviderState | null>(null);

function connectionFromRef(ref: AdminProviderRef, existing?: AdminProviderConnection): AdminProviderConnection {
  return {
    id: ref.id,
    baseUrl: ref.backendUrl,
    label: ref.label,
    auth: existing?.auth ?? { kind: "session", hasCredential: false },
    ...(existing?.contractVersion ? { contractVersion: existing.contractVersion } : {}),
    addedAt: existing?.addedAt ?? Date.now(),
  };
}

function reconcileRegistry(
  deployment: ProviderDeployment,
  stored: AdminProviderRegistry,
  hostnamePinned: boolean,
): AdminProviderRegistry {
  const storedById = new Map(stored.connections.map((connection) => [connection.id, connection]));
  let registry: AdminProviderRegistry = {
    connections: deployment.providers.map((provider) => connectionFromRef(provider, storedById.get(provider.id))),
    activeId: deployment.activeProviderId,
  };

  // A hostname-selected deployment is intentionally pinned to that build-owned
  // provider. One-URL deployments may restore locally-added connections.
  if (!hostnamePinned) {
    for (const connection of stored.connections) registry = addAdminProvider(registry, connection);
    if (stored.activeId) registry = setActiveAdminProvider(registry, stored.activeId);
  }
  return registry;
}

function replaceConnection(
  registry: AdminProviderRegistry,
  id: string,
  update: (connection: AdminProviderConnection) => AdminProviderConnection,
): AdminProviderRegistry {
  let changed = false;
  const connections = registry.connections.map((connection) => {
    if (connection.id !== id) return connection;
    changed = true;
    return update(connection);
  });
  return changed ? { ...registry, connections } : registry;
}

function providerIdFor(label: string, registry: AdminProviderRegistry): string {
  const slug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "provider";
  let id = slug;
  let suffix = 2;
  while (registry.connections.some((connection) => connection.id === id)) id = `${slug}-${suffix++}`;
  return id;
}

/** Owns provider selection, non-secret registry metadata, and per-provider negotiation. */
export function ProviderProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const resolved = useMemo(
    () => resolveProviderDeployment(ADMIN_PROVIDER_CONFIG, window.location.hostname),
    [],
  );
  const hostname = window.location.hostname.trim().toLowerCase();
  const hostnameProviderId = ADMIN_PROVIDER_CONFIG.hostnameMap?.[hostname];
  const hostnamePinned = !!hostnameProviderId && resolved.providers.some(
    (provider) => provider.id === hostnameProviderId,
  );
  const store = useMemo(() => new LocalAdminProviderRegistryStore(), []);
  const [registry, setRegistry] = useState<AdminProviderRegistry>(
    () => reconcileRegistry(resolved, store.loadSnapshot(), hostnamePinned),
  );
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [capabilitiesWarning, setCapabilitiesWarning] = useState<string | null>(null);
  const initialNegotiated = useRef(false);

  useEffect(() => {
    void store.save(registry);
  }, [registry, store]);

  const activeConnection = resolveActiveAdminProvider(registry);
  const provider = activeConnection
    ? { id: activeConnection.id, label: activeConnection.label, backendUrl: activeConnection.baseUrl }
    : null;
  const multiProviderEnabled = !hostnamePinned && (resolved.mode === "multi" || registry.connections.length > 1);
  const deployment = useMemo<ProviderDeployment>(
    () => ({
      ...resolved,
      mode: multiProviderEnabled ? "multi" : "single",
      providers: registry.connections.map((connection) => ({
        id: connection.id,
        label: connection.label,
        backendUrl: connection.baseUrl,
      })),
      backendOrigins: registry.connections.map((connection) => new URL(connection.baseUrl).origin),
      activeProvider: provider,
      activeProviderId: provider?.id ?? null,
      showSwitcher: multiProviderEnabled,
    }),
    [multiProviderEnabled, provider, registry.connections, resolved],
  );

  const capabilities = useMemo<AdminCapabilitiesResult | null>(() => {
    // The unchanged, one-provider default stays unrestricted exactly as before v1.
    // Capability gates become authoritative only when the switcher model is active.
    if (!multiProviderEnabled || !activeConnection) return null;
    return resolveAdminCapabilities({ contractVersion: activeConnection.contractVersion });
  }, [activeConnection, multiProviderEnabled]);

  const negotiate = useCallback(async (connection: AdminProviderConnection, tolerateUnavailable = true) => {
    setCapabilitiesLoading(true);
    setCapabilitiesWarning(null);
    try {
      const metadata = await new BackendClient(connection.baseUrl).providerMetadata();
      const result = resolveAdminCapabilitiesFromMetadata(metadata);
      setRegistry((current) => replaceConnection(current, connection.id, (candidate) => {
        const next = { ...candidate };
        if (metadata.contractVersion) next.contractVersion = metadata.contractVersion;
        else delete next.contractVersion;
        return next;
      }));
      return { result, contractVersion: metadata.contractVersion };
    } catch (error) {
      const legacyEndpoint = error instanceof BackendError && error.status === 404;
      if (!tolerateUnavailable && !legacyEndpoint) {
        setCapabilitiesWarning(null);
        throw new Error(`Could not connect to provider metadata. ${(error as Error).message}`);
      }
      setRegistry((current) => replaceConnection(current, connection.id, (candidate) => {
        const next = { ...candidate };
        delete next.contractVersion;
        return next;
      }));
      const detail = legacyEndpoint
        ? "Provider metadata is unavailable; using the legacy capability baseline."
        : `Could not read provider metadata; using the legacy capability baseline. ${(error as Error).message}`;
      setCapabilitiesWarning(detail);
      return { result: resolveAdminCapabilities(), contractVersion: undefined };
    } finally {
      setCapabilitiesLoading(false);
    }
  }, []);

  // Negotiate the initially selected provider once in a multi-provider deployment.
  useEffect(() => {
    if (!multiProviderEnabled || !activeConnection || initialNegotiated.current) return;
    initialNegotiated.current = true;
    void negotiate(activeConnection);
  }, [activeConnection, multiProviderEnabled, negotiate]);

  const setActiveProviderId = useCallback(async (providerId: string) => {
    const connection = registry.connections.find((candidate) => candidate.id === providerId);
    if (!connection) return;

    await queryClient.cancelQueries();
    await queryClient.invalidateQueries({ refetchType: "none" });
    queryClient.clear();
    setRegistry((current) => setActiveAdminProvider(current, providerId));
    await negotiate(connection);
  }, [negotiate, queryClient, registry.connections]);

  const addProvider = useCallback(async ({ label, baseUrl }: AddProviderInput) => {
    const normalized = normalizeAdminBaseUrl(baseUrl);
    const trimmedLabel = label.trim();
    if (!trimmedLabel) throw new Error("Enter a provider label.");
    if (!normalized) throw new Error("Enter a valid http(s) provider URL.");
    if (registry.connections.some((connection) => normalizeAdminBaseUrl(connection.baseUrl) === normalized)) {
      throw new Error("That provider is already registered.");
    }

    const connection: AdminProviderConnection = {
      id: providerIdFor(trimmedLabel, registry),
      baseUrl: normalized,
      label: trimmedLabel,
      auth: { kind: "session", hasCredential: false },
      addedAt: Date.now(),
    };
    const negotiated = await negotiate(connection, false);
    // `negotiate` cannot update a not-yet-added row, so carry its handshake result
    // into the connection that is about to enter the registry.
    const finalConnection = negotiated.contractVersion
      ? { ...connection, contractVersion: negotiated.contractVersion }
      : connection;
    await queryClient.cancelQueries();
    await queryClient.invalidateQueries({ refetchType: "none" });
    queryClient.clear();
    setRegistry((current) => {
      const added = addAdminProvider(current, finalConnection);
      return setActiveAdminProvider(added, finalConnection.id);
    });
  }, [negotiate, queryClient, registry]);

  const setProviderAuth = useCallback((kind: "session" | "adminKey", hasCredential: boolean) => {
    const activeId = registry.activeId;
    if (!activeId) return;
    setRegistry((current) => replaceConnection(current, activeId, (connection) => ({
      ...connection,
      auth: { kind, hasCredential },
    })));
  }, [registry.activeId]);

  const value = useMemo<ProviderState>(
    () => ({
      deployment,
      registry,
      provider,
      activeConnection,
      capabilities,
      capabilitiesLoading,
      capabilitiesWarning,
      setActiveProviderId,
      addProvider,
      setProviderAuth,
    }),
    [
      activeConnection,
      addProvider,
      capabilities,
      capabilitiesLoading,
      capabilitiesWarning,
      deployment,
      provider,
      registry,
      setActiveProviderId,
      setProviderAuth,
    ],
  );

  return <ProviderContext.Provider value={value}>{children}</ProviderContext.Provider>;
}

export function useProvider(): ProviderState {
  const context = useContext(ProviderContext);
  if (!context) throw new Error("useProvider() must be used within <ProviderProvider>");
  return context;
}
